import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Brand colours and labels
const BRAND_STYLE = {
  coles:      { color: '#e01a22', label: 'Coles' },
  woolworths: { color: '#178841', label: 'Woolworths' },
  aldi:       { color: '#00005a', label: 'ALDI' },
  iga:        { color: '#7b1a24', label: 'IGA' },
};

/**
 * SupermarketLayer
 *
 * Renders supermarket pins on the Leaflet map, clustered at low zoom.
 * Fetches from /api/supermarkets based on the current map bounds and
 * the selected brands.
 *
 * Props:
 *   brands  - array of brand keys to show, e.g. ['coles','woolworths']
 */
export default function SupermarketLayer({ brands = [] }) {
  const map = useMap();
  const clusterRef = useRef(null);
  const fetchAbortRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // If no brands selected, remove any existing cluster and return
    if (!brands || brands.length === 0) {
      if (clusterRef.current) {
        map.removeLayer(clusterRef.current);
        clusterRef.current = null;
      }
      return;
    }

    // Create the cluster group if it doesn't exist
    if (!clusterRef.current) {
      clusterRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 14,
      });
      map.addLayer(clusterRef.current);
    }

    const cluster = clusterRef.current;

    // Fetch supermarkets for the current bounds + selected brands
    const fetchSupermarkets = async () => {
      // Cancel previous fetch
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      const bounds = map.getBounds();
      const south = bounds.getSouth();
      const west = bounds.getWest();
      const north = bounds.getNorth();
      const east = bounds.getEast();

      const url = `/api/supermarkets?bounds=${south},${west},${north},${east}&brand=${brands.join(',')}`;

      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const supermarkets = data.supermarkets || [];

        // Clear existing markers, add new ones
        cluster.clearLayers();

        supermarkets.forEach((s) => {
          const style = BRAND_STYLE[s.brand] || { color: '#666666', label: s.brand };

          const icon = L.divIcon({
            className: 'supermarket-marker',
            html: `<div style="
              background-color: ${style.color};
              color: white;
              border-radius: 50%;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              font-weight: 600;
              ">🛒</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12],
          });

          const marker = L.marker([s.latitude, s.longitude], { icon });

          const addressParts = [
            s.address?.street,
            s.address?.suburb,
            s.address?.state,
            s.address?.postcode,
          ].filter(Boolean);

          const addressLine = addressParts.length > 0
            ? `<div class="text-xs text-gray-600 mt-1">${addressParts.join(', ')}</div>`
            : '';

          const popupContent = `
            <div class="max-w-[220px]">
              <div class="flex items-center gap-2">
                <span style="
                  background-color: ${style.color};
                  color: white;
                  border-radius: 50%;
                  width: 24px;
                  height: 24px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 12px;
                  flex-shrink: 0;
                  ">🛒</span>
                <div>
                  <div class="font-semibold text-sm">${s.name || style.label}</div>
                  <div class="text-xs text-gray-500">${style.label}</div>
                </div>
              </div>
              ${addressLine}
            </div>
          `;

          marker.bindPopup(popupContent);
          cluster.addLayer(marker);
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Supermarket fetch error:', err);
        }
      }
    };

    fetchSupermarkets();

    // Refetch when the map is moved or zoomed
    const onMoveEnd = () => fetchSupermarkets();
    map.on('moveend', onMoveEnd);

    return () => {
      map.off('moveend', onMoveEnd);
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, [map, brands]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clusterRef.current && map) {
        map.removeLayer(clusterRef.current);
        clusterRef.current = null;
      }
    };
  }, [map]);

  return null;
}
